/* eslint-disable no-nested-ternary */
import {
  Autocomplete,
  Box,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  Grid,
  Icon,
  Radio,
  RadioGroup,
  Stack,
  styled,
  Switch,
  TextField,
} from "@mui/material";
import Modal from "@mui/material/Modal";
import sample from "assets/images/sample.png";
import MDAvatar from "components/MDAvatar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import { useMaterialUIController } from "context";
import { FieldArray, Form, Formik } from "formik";
import momentTz from "moment-timezone";
import { enqueueSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { teamsAction } from "redux/slice/teams";
import TooltipLight from "utils/TooltipLight";
import * as Yup from "yup";

import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import MuiAccordionSummary from "@mui/material/AccordionSummary";
import { createTeamMember } from "apis/team";
import AsteriskLabel from "components/AsteriskLabel";
import MDDatePicker from "components/MDDatePicker";
import MDTypography from "components/MDTypography";
import { getAPICall, putAPICall } from "lib/callApi";
import {
  COMPANY_USERS,
  GET_ALL_PERMISSION,
  GET_KPI_SELECTION_OPTIONS,
  GET_PERMISSION_PROFILE_GRANTS,
  GET_PERMISSION_PROFILES,
  GET_QUOTA_SETTINGS,
  PERMISSIONS_NAME,
  QUOTA_PERIODS,
  USER,
  USER_ROLES,
  WORKSPACE_DROPDOWN_LISTS,
} from "lib/constants";
import moment from "moment";
import { loginUserAction } from "redux/slice/users";
import { displayDate, FALLBACK_DATE_FORMAT } from "utils/Date";
import { checkPermission, formatNumberWithComma } from "utils/HelperFunctions";
import Loader from "utils/Loader";
import { checkUserModuleComponentEnabledById } from "utils/Modules";
import "../../../styles/teams.scss";

const Accordion = styled((props) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  "&:before": {
    display: "none",
  },
}));

const AccordionSummary = styled((props) => (
  <MuiAccordionSummary expandIcon={<ArrowRightIcon />} {...props} />
))(() => ({
  backgroundColor: "#fff",
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(() => ({
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

function AddTeamMemberModal({
  open,
  setOpen,
  userToEdit,
  allowAccess,
  updatePendingUserAPIRequest,
  initialState = {},
  teamId: id,
}) {
  const isAddNew = initialState?.isAddNew;
  const loginUser = useSelector((state) => state.loginUser.loginUserState);
  const modules = useSelector((state) => state.modules.enabledModules);
  const teams = useSelector((state) => state.teams.allTeams || state.teams.teams);
  const [userRoles, setUserRoles] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [permissionProfiles, setPermissionProfiles] = useState([]);
  const [quotaPeriods, setQuotaPeriods] = useState([]);
  const [user, setUser] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const dispatch = useDispatch();
  const [initialValues, setInitialValues] = useState({
    memberName: "",
    memberEmail: "",
    role: {},
    permissionsProfile: {},
    timeAtCompany: {},
    averageSalesCycleLength: {},
    type: null,
    team: teams?.length > 0 && id ? teams?.find((t) => t.id === parseInt(id, 10)) : {},
    permissions: [],
    joinDate: moment().toDate(),
    reportsTo: null,
    annualQuota: "",
    quotaPeriod: null,
    quotaOption: "revenue",
    kpi: null,
    kpiQuota: 0,
  });
  const [userFetchLoading, setUserFetchLoading] = useState(false);
  const [companyUsers, setCompanyUsers] = useState([]);
  const [permissionFetchLoading, setPermissionFetchLoading] = useState(false);
  const [permissionsList, setPermissionsList] = useState([]);
  const [permissionProfileGrants, setPermissionProfileGrants] = useState([]);
  const [roleWisePermissionFetchLoading, setPermissionProfileGrantsFetchLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [timeAtCompanyOptions, setTimeAtCompanyOptions] = useState([]);
  const [averageSalesCycleLengthOptions, setAverageSalesCycleLengthOptions] = useState([]);
  const [requireCompanyTenure, setRequireCompanyTenure] = useState(false);
  const [requireSalesCycle, setRequireSalesCycle] = useState(false);
  const [quotaSource, setQuotaSource] = useState("manual");

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const [emailExists, setEmailExists] = useState(false);
  const handleClose = () => {
    if (!isLoading) {
      setEmailExists(false);
      setOpen(false);
      setUser();
    }
  };

  const validationSchema = Yup.object({
    memberName: Yup.string()
      .max(100, "Full name cannot be more than 100 characters")
      .required("Name is required"),
    memberEmail: Yup.string()
      .email("Enter a valid email")
      .max(254, "Email must be less than 254 characters")
      .required("Employee Email is required"),
    role: Yup.object({
      id: Yup.string().required("Role is required"),
    }),
    team: Yup.object().nullable(),
    reportsTo: Yup.object().nullable(),
    annualQuota: Yup.string()
      .test("is-number", "Annual Quota must be a number", (value) => {
        if (!value) return true;
        const noCommas = value.replace(/,/gi, "");
        return !Number.isNaN(parseFloat(noCommas));
      })
      .nullable(),
    kpi: Yup.object().when("quotaOption", {
      is: "kpi",
      then: Yup.object().nullable().required("Target KPI is required"),
      otherwise: Yup.object().nullable(),
    }),
    kpiQuota: Yup.string().when("quotaOption", {
      is: "kpi",
      then: Yup.string()
        .test("is-number", "Annual KPI Quota must be a number", (value) => {
          if (!value) return true;
          const noCommas = value.replace(/,/gi, "");
          return !Number.isNaN(parseFloat(noCommas));
        })
        .nullable(),
      otherwise: Yup.string().nullable(),
    }),
    timeAtCompany: requireCompanyTenure
      ? Yup.object().nullable().required("Time at company is required")
      : Yup.object().nullable(),
    averageSalesCycleLength: requireSalesCycle
      ? Yup.object().nullable().required("Sales cycle is required")
      : Yup.object().nullable(),
  });

  const [controller] = useMaterialUIController();

  const { darkMode } = controller;

  const fetchUserRoles = async () => {
    try {
      const roles = await getAPICall(USER_ROLES, true);
      if (roles?.data?.success) {
        setUserRoles(roles.data.data);
      } else {
        handleClose();
        if (roles.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: roles.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (error) {
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchKpis = async () => {
    try {
      const result = await getAPICall(GET_KPI_SELECTION_OPTIONS, true);
      if (result?.data?.success) {
        setKpis(result.data.data);
      } else {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: result?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (error) {
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchPermissionProfiles = async () => {
    try {
      const permissionProfilesResponse = await getAPICall(GET_PERMISSION_PROFILES, true);
      if (permissionProfilesResponse?.data?.success) {
        setPermissionProfiles(permissionProfilesResponse.data.data);
      } else {
        handleClose();
        if (permissionProfilesResponse.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: permissionProfilesResponse.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (error) {
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchQuotaPeriods = async () => {
    try {
      const periods = await getAPICall(QUOTA_PERIODS, true);
      if (periods?.data?.success) {
        setQuotaPeriods(periods.data.data);
      } else {
        handleClose();
        if (periods.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: periods.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (error) {
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchQuotaSettings = async () => {
    try {
      const quotaSettings = await getAPICall(GET_QUOTA_SETTINGS, true);
      if (quotaSettings?.data?.success) {
        setQuotaSource(quotaSettings.data.quotaSource);
      } else {
        handleClose();
        if (quotaSettings.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: quotaSettings.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (error) {
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchCompanyUsers = async () => {
    try {
      const users = await getAPICall(COMPANY_USERS, true);
      if (users?.data?.success) {
        setCompanyUsers(users.data.data);
      } else {
        handleClose();
        if (users.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: users.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
    } catch (error) {
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchUser = async () => {
    try {
      setUserFetchLoading(true);
      const response = await getAPICall(`${USER}/${userToEdit}`, true);
      if (response?.data?.success) {
        const returnedUser = response.data.data;
        if (returnedUser.UserCompaniesLinks?.FK_user_company_tenure_years_id) {
          setRequireCompanyTenure(true);
        }
        if (returnedUser.UserCompaniesLinks?.FK_user_sales_cycles_id) {
          setRequireSalesCycle(true);
        }
        setUser(returnedUser);
      } else {
        handleClose();
        if (response.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: response.response.data.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
      setUserFetchLoading(false);
    } catch (error) {
      handleClose();
      setUserFetchLoading(false);
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchAllPermission = async () => {
    try {
      setPermissionFetchLoading(true);
      const data = await getAPICall(GET_ALL_PERMISSION, true);

      if (data?.data?.success) {
        setPermissionsList(data.data.data.permissionsList);
      } else {
        handleClose();
        if (data.response.status !== 403) {
          enqueueSnackbar(
            JSON.stringify({
              icon: "error",
              title: "Error",
              text: data?.response?.data?.message || "Something Went Wrong",
            }),
            {
              variant: "custom",
            }
          );
        }
      }
      setPermissionFetchLoading(false);
    } catch (error) {
      setPermissionFetchLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const fetchPermissionProfileGrants = async () => {
    try {
      setPermissionProfileGrantsFetchLoading(true);
      const data = await getAPICall(GET_PERMISSION_PROFILE_GRANTS, true);

      if (data?.data?.success) {
        setPermissionProfileGrants(data.data.data.permissionsList);
      } else {
        handleClose();
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: data?.response?.data?.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
      setPermissionProfileGrantsFetchLoading(false);
    } catch (error) {
      setPermissionProfileGrantsFetchLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  const isSwitchChecked = (permission, values) => {
    try {
      if (
        values.find((x) => x.permission === PERMISSIONS_NAME.ADMIN).allow &&
        permission.permission !== PERMISSIONS_NAME.INPUT_KPIS
      ) {
        return true;
      }
      return permission.allow;
    } catch (err) {
      return false;
    }
  };

  const isSwitchDisabled = (permission, values) => {
    try {
      if (permission.disabled && permission.moduleCheck) {
        return true;
      }

      if (
        values.find((x) => x.permission === PERMISSIONS_NAME.ADMIN).allow &&
        permission.permission !== PERMISSIONS_NAME.INPUT_KPIS &&
        permission.permission !== PERMISSIONS_NAME.ADMIN
      ) {
        return true;
      }
      return permission.disabled;
    } catch (err) {
      return false;
    }
  };

  const initialDisabledState = (permission) => {
    try {
      if (
        permission.permission === PERMISSIONS_NAME.ADMIN &&
        !checkPermission(PERMISSIONS_NAME.ADMIN, loginUser?.userPermission)
      ) {
        return { disabled: true };
      }

      if (permission.FK_modules_id && !permission.FK_modules_components_id) {
        return {
          disabled: !checkUserModuleComponentEnabledById(modules, permission.FK_modules_id),
          moduleCheck: true,
        };
      }

      if (permission.FK_modules_id && permission.FK_modules_components_id) {
        return {
          disabled: !checkUserModuleComponentEnabledById(
            modules,
            permission.FK_modules_id,
            permission.FK_modules_components_id
          ),
          moduleCheck: true,
        };
      }

      return { disabled: false };
    } catch {
      return { disabled: false };
    }
  };

  const fetchWorkspaceDropdownsLists = async () => {
    try {
      const allDropDownLists = await getAPICall(
        `${WORKSPACE_DROPDOWN_LISTS}?include=timeAtCompany,averageSalesCycleLength`,
        true
      );
      if (allDropDownLists?.data?.success) {
        setTimeAtCompanyOptions(allDropDownLists.data?.data?.timeAtCompany || []);
        setAverageSalesCycleLengthOptions(
          allDropDownLists.data?.data?.averageSalesCycleLength || []
        );
      } else if (allDropDownLists.response.status !== 403) {
        enqueueSnackbar(
          JSON.stringify({
            icon: "error",
            title: "Error",
            text: allDropDownLists.response.data.message || "Something Went Wrong",
          }),
          {
            variant: "custom",
          }
        );
      }
    } catch (err) {
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: err.message || "Something Went Wrong",
        }),
        {
          variant: "custom",
        }
      );
    }
  };

  useEffect(() => {
    if (isAddNew && permissionProfiles?.length && permissionProfileGrants?.length) {
      const basicPermissionProfile = permissionProfiles.find(
        (permissionProfile) => permissionProfile.profile_name === "Basic"
      );

      const basicPermissionGrants = permissionProfileGrants.filter(
        (permissionGrant) => permissionGrant.FK_permission_profiles_id === basicPermissionProfile.id
      );

      const permissions = permissionsList
        .map((permission) => {
          const foundPermissionGrant = basicPermissionGrants.find(
            (permissionGrant) => permissionGrant.FK_permissions_id === permission.id
          );

          return {
            ...permission,
            ...initialDisabledState(permission),
            allow: foundPermissionGrant.allow,
          };
        })
        .reduce((dict, permission) => {
          dict[permission.id] = permission;
          return dict;
        }, {});

      setInitialValues((prev) => ({
        ...prev,
        permissions: Object.values(permissions),
        permissionsProfile: basicPermissionProfile,
      }));
    }
  }, [isAddNew, permissionProfiles, permissionProfileGrants]);

  useEffect(() => {
    if (open) {
      fetchUserRoles();
      fetchKpis();
      fetchPermissionProfiles();
      fetchCompanyUsers();
      fetchAllPermission();
      fetchPermissionProfileGrants();
      fetchQuotaPeriods();
      fetchWorkspaceDropdownsLists();
      fetchQuotaSettings();
      if (userToEdit) {
        fetchUser();
      }
    }
  }, [open]);

  // allUserQuotas's id should be in ascending order
  const findLatestQuotaObj = (allUserQuotas, isForRevenue) => {
    if (isForRevenue) {
      // Filter revenue objects and return the latest
      const revenueObjects = allUserQuotas.filter((obj) => obj.FK_kpi_actions_id === null);
      return revenueObjects.length > 0 ? revenueObjects[revenueObjects.length - 1] : null;
    }

    // Filter kpi objects and return the latest
    const kpiObjects = allUserQuotas.filter((obj) => obj.FK_kpi_actions_id !== null);
    return kpiObjects.length > 0 ? kpiObjects[kpiObjects.length - 1] : null;
  };

  useEffect(() => {
    if (user) {
      // Set up quota here
      // allUserQuotas might contain both revenue and kpi quotas, id in ascending order, or could be empty as well
      const allUserQuotas = user?.UserCompaniesLinks?.UserQuotas;
      const latestUserQuota = allUserQuotas?.length && allUserQuotas[allUserQuotas.length - 1];
      let quotaOption;
      let quota;
      let kpiQuota;
      let kpi;
      // latestUserQuota could be undefined when there is no user quota record
      if (latestUserQuota) {
        quotaOption = latestUserQuota?.FK_kpi_actions_id ? "kpi" : "revenue";
      } else {
        quotaOption = null;
      }

      if (quotaOption === "revenue") {
        quota = formatNumberWithComma(latestUserQuota.amount);
        const kpiQuotaObj = findLatestQuotaObj(allUserQuotas, false);
        kpiQuota = kpiQuotaObj ? formatNumberWithComma(kpiQuotaObj.amount) : null;
        kpi = kpiQuotaObj ? { id: kpiQuotaObj.FK_kpi_actions_id } : null;
      } else if (quotaOption === "kpi") {
        kpi = { id: latestUserQuota.FK_kpi_actions_id };
        kpiQuota = formatNumberWithComma(latestUserQuota.amount);
        const quotaObj = findLatestQuotaObj(allUserQuotas, true);
        quota = quotaObj ? formatNumberWithComma(quotaObj.amount) : null;
      } else {
        quota = null;
        kpiQuota = null;
        kpi = null;
      }

      setInitialValues((prev) => {
        const userCompanyLink = user.UserCompaniesLinks;
        const shownPermissionProfile =
          userCompanyLink?.PermissionProfile || prev.permissionsProfile;
        const permissions = (
          shownPermissionProfile?.id
            ? Object.values(
                permissionProfileGrants
                  .filter(
                    (permissionProfileGrant) =>
                      permissionProfileGrant.FK_permission_profiles_id === shownPermissionProfile.id
                  )
                  .reduce((dict, permissionProfileGrant) => {
                    const dictKey = `${permissionProfileGrant.FK_permission_profiles_id}:${permissionProfileGrant.FK_permissions_id}`;
                    if (!dict[dictKey]) {
                      dict[dictKey] = permissionProfileGrant;
                    }
                    // If they set up permission on company level, we want to overwrit with the value
                    if (permissionProfileGrant.FK_companies_id) {
                      dict[dictKey] = permissionProfileGrant;
                    }

                    return dict;
                  }, {})
              ).map((permissionProfileGrant) => {
                const foundPermissionDetails = permissionsList.find(
                  (permission) => permission.id === permissionProfileGrant.FK_permissions_id
                );

                return {
                  ...foundPermissionDetails,
                  allow: permissionProfileGrant.allow,
                };
              })
            : permissionsList
        )
          .map((permission) => ({
            ...permission,
            ...initialDisabledState(permission),
          }))
          .reduce((dict, permission) => {
            dict[permission.id] = permission;
            return dict;
          }, {});

        // Now that we have the base set of permissions either from default or permission profile, show user overrides
        if (user.permissions) {
          user.permissions.forEach((p) => {
            permissions[p.FK_permissions_id] = {
              ...permissions[p.FK_permissions_id],
              allow: p.allow,
            };
          });
        }

        return {
          memberName: userCompanyLink?.name,
          memberEmail: user.email,
          role:
            userRoles.length &&
            userRoles?.find((role) => role?.id === userCompanyLink?.FK_user_company_roles_id),
          permissionsProfile: shownPermissionProfile,
          type: user.type[0].toUpperCase() + user.type.slice(1),
          team: teams?.find((t) => t.id === parseInt(userCompanyLink.FK_teams_id, 10)),
          joinDate: moment(userCompanyLink.company_join_date).toDate(),
          reportsTo:
            companyUsers?.length &&
            companyUsers?.find(
              (o) => o.id === userCompanyLink.FK_reports_to_user_companies_links_id
            ),
          permissions: Object.values(permissions),
          quotaOption,
          annualQuota: quota,
          kpi,
          kpiQuota,
          quotaPeriod: userCompanyLink.FK_quota_periods_period
            ? { period: userCompanyLink.FK_quota_periods_period }
            : null,
          timeAtCompany: timeAtCompanyOptions?.find(
            (timeAtCompanyOption) =>
              timeAtCompanyOption?.value === userCompanyLink.FK_user_company_tenure_years_id
          ),
          averageSalesCycleLength: averageSalesCycleLengthOptions?.find(
            (averageSalesCycleLengthOption) =>
              averageSalesCycleLengthOption?.value === userCompanyLink.FK_user_sales_cycles_id
          ),
        };
      });
    }
  }, [
    user,
    userRoles,
    permissionsList,
    permissionProfileGrants,
    companyUsers,
    timeAtCompanyOptions,
    averageSalesCycleLengthOptions,
  ]);

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: window.innerWidth < 600 ? "100%" : 500,
    bgcolor: darkMode ? "#1F283E" : "background.paper",
    boxShadow: 24,
    outline: "none",
    p: 4,
    maxHeight: "90%",
    overflowX: "auto",
    borderRadius: "0.25em",
  };

  const handleTeamMemberCreate = async (data) => {
    try {
      setIsLoading(true);
      let annualQuota = 0;
      let kpiId = 0;
      if (data.quotaOption === "revenue") {
        annualQuota = data.annualQuota ? data?.annualQuota.replace(/,/gi, "") : 0;
      } else if (data.quotaOption === "kpi") {
        annualQuota = data.kpiQuota ? data.kpiQuota.replace(/,/gi, "") : 0;
        kpiId = data.kpi ? data.kpi.id : 0;
      }
      const newTeamMember = await createTeamMember({
        ...data,
        timeAtCompany: data?.timeAtCompany?.value,
        averageSalesCycleLength: data?.averageSalesCycleLength?.value,
        teamId: data?.team?.id,
        type: "user",
        companyId: loginUser.companyId,
        annualQuota,
        kpiId,
        timezone: momentTz.tz.guess(),
      });
      if (newTeamMember?.data?.success) {
        handleClose();
        setIsLoading(false);
        if (data?.team?.id === parseInt(id, 10) && id) {
          dispatch(teamsAction.getTeamSaga({ pageNumber: 0, pageSize: 10, id }));
        }
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: "Team Member is added successfully!",
          }),
          {
            variant: "custom",
          }
        );
      } else {
        setIsLoading(false);
        if (newTeamMember?.response?.status === 409) {
          setEmailExists(true);
        } else {
          handleClose();
          if (newTeamMember?.response?.status !== 403) {
            enqueueSnackbar(
              JSON.stringify({
                icon: newTeamMember?.response?.status === 422 ? "info" : "error",
                title: "Error",
                text: newTeamMember?.response?.data?.message || "Something Went Wrong",
              }),
              {
                variant: "custom",
              }
            );
            handleClose();
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
      handleClose();
    }
  };

  const handleTeamMemberUpdate = async (data) => {
    try {
      const isPermissionProfileChange =
        data?.permissionsProfile?.id !== user?.UserCompaniesLinks?.PermissionProfile?.id;

      setIsLoading(true);
      let annualQuota = 0;
      let kpiId = 0;
      if (data.quotaOption === "revenue") {
        annualQuota = data.annualQuota ? data?.annualQuota.replace(/,/gi, "") : 0;
      } else if (data.quotaOption === "kpi") {
        annualQuota = data.kpiQuota ? data.kpiQuota.replace(/,/gi, "") : 0;
        kpiId = data.kpi ? data.kpi.id : 0;
      }
      const apiData = {
        ...data,
        timeAtCompany: data?.timeAtCompany?.value || null,
        averageSalesCycleLength: data?.averageSalesCycleLength?.value || null,
        teamId: data?.team?.id,
        type: "user",
        annualQuota,
        kpiId,
        isPermissionProfileChange,
      };

      const updatedData = allowAccess
        ? await updatePendingUserAPIRequest(userToEdit, { ...apiData, allowAccess: true })
        : await putAPICall(`${USER}/${userToEdit}`, true, apiData);

      if (updatedData?.data?.success) {
        handleClose();
        setIsLoading(false);
        dispatch(teamsAction.getTeamSaga({ pageNumber: 0, pageSize: 10, id: data?.team?.id }));

        if (userToEdit === loginUser.id) {
          dispatch(loginUserAction.getLoginUserData(updatedData?.data?.data));
        }
        // fire event so we can retrieve list for settings dropdown
        window.dispatchEvent(new Event("permissionsUpdated"));
        enqueueSnackbar(
          JSON.stringify({
            icon: "success",
            title: "Success",
            text: "Team Member is updated successfully!",
          }),
          {
            variant: "custom",
          }
        );
      } else {
        setIsLoading(false);
        if (updatedData?.response?.status === 409) {
          setEmailExists(true);
        } else {
          handleClose();
          if (updatedData?.response?.status !== 403) {
            enqueueSnackbar(
              JSON.stringify({
                icon: updatedData?.response?.status === 422 ? "info" : "error",
                title: "Error",
                text: updatedData?.response?.data?.message || "Something Went Wrong",
              }),
              {
                variant: "custom",
              }
            );
            handleClose();
          }
        }
      }
    } catch (error) {
      setIsLoading(false);
      handleClose();
      enqueueSnackbar(
        JSON.stringify({
          icon: "error",
          title: "Error",
          text: error?.response?.data.message || error.message,
        }),
        {
          variant: "custom",
        }
      );
      handleClose();
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <MDBox sx={style}>
        <Loader
          loading={userFetchLoading || permissionFetchLoading || roleWisePermissionFetchLoading}
        />
        <MDBox sx={{ display: "flex", justifyContent: "center", alignItems: "center" }} mb={2}>
          <MDAvatar
            src={user?.UserCompaniesLinks?.profile_picture || sample}
            alt="profile-image"
            size="xl"
            shadow="sm"
          />
        </MDBox>
        <Formik
          enableReinitialize
          validateOnChange
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={userToEdit ? handleTeamMemberUpdate : handleTeamMemberCreate}
        >
          {({ values, setFieldValue, touched, errors, dirty }) => (
            <Form>
              <Stack
                spacing={2}
                sx={{
                  "& .MuiFormHelperText-root": { color: "#d32f2f" },
                  "& .MuiInputLabel-formControl": { lineHeight: "20px !important" },
                }}
              >
                <MDInput
                  name="memberName"
                  label="Full name *"
                  value={values?.memberName}
                  onChange={(event) => {
                    setFieldValue("memberName", event.target.value);
                  }}
                  fullWidth
                  error={touched.memberName && Boolean(errors.memberName)}
                  helperText={touched.memberName && errors.memberName}
                />
                <MDInput
                  name="memberEmail"
                  label="Employee email *"
                  value={values?.memberEmail}
                  onChange={(event) => {
                    setFieldValue("memberEmail", event.target.value);
                  }}
                  fullWidth
                  error={touched.memberEmail && Boolean(errors.memberEmail)}
                  helperText={touched.memberEmail && errors.memberEmail}
                />
                <Autocomplete
                  fullWidth
                  id="role"
                  disableClearable
                  options={userRoles}
                  disabled={Boolean(values.type && values.type !== "User")}
                  value={values.role}
                  getOptionLabel={(option) => option?.role_name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(_, value) => {
                    setFieldValue("role", value || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Role *"
                      error={!values.role?.id && touched.role && Boolean(errors.role?.id)}
                      helperText={!values.role?.id && touched.role && errors.role?.id}
                    />
                  )}
                />
                <Accordion
                  className="edit-permission-accordion"
                  expanded={expanded === "panel2"}
                  onChange={handleChange("panel2")}
                >
                  <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
                    <MDTypography className="title">About Role</MDTypography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <MDBox sx={{ display: "flex" }} mx={2}>
                      <MDBox mr={1} sx={{ width: "100%" }}>
                        <Autocomplete
                          fullWidth
                          id="timeAtCompany"
                          options={timeAtCompanyOptions}
                          value={values.timeAtCompany}
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) => option?.value === value?.value}
                          onChange={(_, option) => {
                            setFieldValue("timeAtCompany", option || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label="Time at company"
                              error={touched.timeAtCompany && Boolean(errors.timeAtCompany)}
                              helperText={touched.timeAtCompany && errors.timeAtCompany}
                            />
                          )}
                        />
                        {errors?.timeAtCompany?.value ? (
                          <FormHelperText style={{ color: "red" }}>
                            {errors.timeAtCompany.value}
                          </FormHelperText>
                        ) : null}
                      </MDBox>
                      <MDBox ml={1} sx={{ width: "100%" }}>
                        <Autocomplete
                          fullWidth
                          id="averageSalesCycleLength"
                          options={averageSalesCycleLengthOptions}
                          value={values?.averageSalesCycleLength}
                          getOptionLabel={(option) => option?.label || ""}
                          isOptionEqualToValue={(option, value) => option?.value === value?.value}
                          onChange={(_, option) => {
                            setFieldValue("averageSalesCycleLength", option || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="standard"
                              label="Average sales cycle length"
                              error={
                                touched.averageSalesCycleLength &&
                                Boolean(errors.averageSalesCycleLength)
                              }
                              helperText={
                                touched.averageSalesCycleLength && errors.averageSalesCycleLength
                              }
                            />
                          )}
                        />
                        {errors?.averageSalesCycleLength?.value ? (
                          <FormHelperText style={{ color: "red" }}>
                            {errors.averageSalesCycleLength.value}
                          </FormHelperText>
                        ) : null}
                      </MDBox>
                    </MDBox>
                  </AccordionDetails>
                </Accordion>
                <Autocomplete
                  fullWidth
                  id="permissionsProfile"
                  disableClearable
                  options={permissionProfiles}
                  disabled={Boolean(values.type && values.type !== "User")}
                  value={values.permissionsProfile}
                  getOptionLabel={(option) => option?.profile_name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(_, value) => {
                    setFieldValue("permissionsProfile", value || null);
                    if (user?.permissionsProfile && user.permissionsProfile.id === value.id) {
                      setFieldValue("permissions", initialValues.permissions);
                    } else {
                      const tempPermissions = permissionsList.map((perm) => ({
                        ...perm,
                        allow: !!permissionProfileGrants.find(
                          (perm1) =>
                            perm1.FK_permissions_id === perm.id &&
                            perm1.FK_permission_profiles_id === value.id
                        )?.allow,
                      }));
                      setFieldValue("permissions", tempPermissions);
                    }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Permissions Profile *"
                      error={
                        !values.permissionsProfile &&
                        touched.permissionsProfile &&
                        Boolean(errors.permissionsProfile)
                      }
                      helperText={
                        !values.permissionsProfile &&
                        touched.permissionsProfile &&
                        errors.permissionsProfile
                      }
                    />
                  )}
                />
                {values?.permissionsProfile && (
                  <Accordion
                    className="edit-permission-accordion"
                    expanded={expanded === "panel1"}
                    onChange={handleChange("panel1")}
                  >
                    <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
                      <MDTypography className="title">Customize User Permissions</MDTypography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={1} mt={1}>
                        <FieldArray
                          name="permission"
                          render={() => (
                            // eslint-disable-next-line react/jsx-no-useless-fragment
                            <>
                              {values?.permissions?.map((permission, index) => (
                                <TooltipLight
                                  key={permission.id}
                                  title={
                                    permission.disabled && permission.moduleCheck
                                      ? "Not available on current Alysio plan!"
                                      : permission.description
                                  }
                                  arrow
                                  disableFocusListener
                                  placement={!(index % 2) ? "left" : "right"}
                                >
                                  <Grid
                                    key={permission.id}
                                    item
                                    xs={12}
                                    sm={6}
                                    md={6}
                                    sx={{
                                      borderRight:
                                        window.innerWidth > 575 && index % 2 === 0
                                          ? "1px solid rgba(0, 0, 0, .25)"
                                          : "",
                                    }}
                                  >
                                    <FormControlLabel
                                      className="permission-item"
                                      control={
                                        <Switch
                                          checked={isSwitchChecked(permission, values.permissions)}
                                          disabled={isSwitchDisabled(
                                            permission,
                                            values.permissions
                                          )}
                                          onChange={(_, checked) => {
                                            setFieldValue(`permissions[${index}].allow`, checked);
                                          }}
                                        />
                                      }
                                      label={permission.displayName}
                                    />
                                  </Grid>
                                </TooltipLight>
                              ))}
                            </>
                          )}
                        />
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}

                <Autocomplete
                  fullWidth
                  id="team"
                  options={teams}
                  disabled={Boolean(values.type && values.type !== "User")}
                  value={values.team}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(_, value) => {
                    setFieldValue("team", value || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Team"
                      error={touched.team && Boolean(errors.team)}
                      helperText={touched.team && errors.team}
                    />
                  )}
                />
                <Autocomplete
                  fullWidth
                  id="reports_to"
                  options={
                    userToEdit
                      ? companyUsers.filter((o) => o?.user?.id !== userToEdit)
                      : companyUsers
                  }
                  value={values.reportsTo}
                  getOptionLabel={(option) => option?.name || ""}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  onChange={(_, value) => {
                    setFieldValue("reportsTo", value || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Reports To"
                      error={touched.reportsTo && Boolean(errors.reportsTo)}
                      helperText={touched.reportsTo && errors.reportsTo}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: params.InputProps.endAdornment,
                      }}
                    />
                  )}
                />
                <Box sx={{ pl: "0.5rem", display: "inline-flex" }}>
                  <FormLabel
                    id="demo-row-radio-buttons-group-label"
                    sx={{ fontSize: "0.9rem", pr: "1rem" }}
                  >
                    Quota Type
                  </FormLabel>
                  <RadioGroup
                    row
                    aria-labelledby="demo-row-radio-buttons-group-label"
                    name="row-radio-buttons-group"
                    value={values.quotaOption}
                    onChange={(_, value) => {
                      setFieldValue("quotaOption", value);
                    }}
                    sx={{
                      mt: "-0.5rem",
                    }}
                  >
                    <FormControlLabel
                      value="revenue"
                      control={<Radio />}
                      label="Revenue"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: "600",
                          color: "#7b809a",
                        },
                        "& .MuiRadio-root": {
                          p: "0.5rem",
                        },
                      }}
                    />
                    <FormControlLabel
                      value="kpi"
                      control={<Radio />}
                      label="KPI"
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: "600",
                          color: "#7b809a",
                        },
                        "& .MuiRadio-root": {
                          p: "0.5rem",
                        },
                      }}
                    />
                  </RadioGroup>
                </Box>
                {values.quotaOption === "kpi" ? (
                  <>
                    <Autocomplete
                      fullWidth
                      options={kpis}
                      getOptionLabel={(option) => option.name}
                      value={
                        (values.kpi?.id && kpis.find((option) => option.id === values.kpi.id)) ||
                        null
                      }
                      onChange={(_, value) => setFieldValue("kpi", value)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Target KPI *"
                          error={touched.kpi && Boolean(errors.kpi)}
                          helperText={touched.kpi && errors.kpi}
                        />
                      )}
                    />
                    <MDInput
                      name="kpiQuota"
                      label="Annual KPI Quota"
                      value={values?.kpiQuota}
                      onChange={(event) => {
                        const value = formatNumberWithComma(event.target.value, false);
                        setFieldValue("kpiQuota", value);
                      }}
                      error={touched.kpiQuota && Boolean(errors.kpiQuota)}
                      fullWidth
                    />
                  </>
                ) : (
                  <>
                    {quotaSource !== "manual" && (
                      <Grid container justifyContent="end">
                        <Grid item>
                          <TooltipLight
                            title={`Quota input is disabled. Quota is being pulled from ${quotaSource}`}
                            arrow
                            disableFocusListener
                            placement="right"
                          >
                            <Icon>help</Icon>
                          </TooltipLight>
                        </Grid>
                      </Grid>
                    )}
                    <MDInput
                      name="annualQuota"
                      label="Annual Revenue Quota"
                      value={values?.annualQuota}
                      onChange={(event) => {
                        const value = formatNumberWithComma(event.target.value);
                        setFieldValue("annualQuota", value);
                      }}
                      disabled={quotaSource !== "manual"}
                      error={touched.annualQuota && Boolean(errors.annualQuota)}
                      fullWidth
                    />
                  </>
                )}
                <Autocomplete
                  fullWidth
                  id="quotaPeriod"
                  disableClearable
                  options={quotaPeriods}
                  value={values.quotaPeriod}
                  getOptionLabel={(option) => option?.period || ""}
                  isOptionEqualToValue={(option, value) => option?.period === value?.period}
                  onChange={(_, value) => {
                    setFieldValue("quotaPeriod", value || null);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Display quota as"
                      error={touched.quotaPeriod && Boolean(errors.quotaPeriod)}
                      helperText={touched.quotaPeriod && errors.quotaPeriod}
                    />
                  )}
                />
                <MDDatePicker
                  input={{
                    placeholder: "Select a date",
                    label: "Join date",
                    value: displayDate(values.joinDate),
                  }}
                  value={values.joinDate}
                  options={{
                    parseDate: (datestr) => moment(datestr, FALLBACK_DATE_FORMAT, true).toDate(),
                    formatDate: (date) => displayDate(date),
                    disableMobile: "true",
                  }}
                  onChange={(e) => {
                    const currTime = moment(e[0])
                      .add(moment().hour(), "hours")
                      .add(moment().minute(), "minutes");
                    setFieldValue("joinDate", currTime.toDate());
                  }}
                />
                {emailExists && (
                  <FormHelperText sx={{ fontSize: "12px" }}>
                    User with this email already exists.
                  </FormHelperText>
                )}
              </Stack>

              <MDBox
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <MDBox>
                  <MDTypography variant="button">
                    <AsteriskLabel postfix>Required fields</AsteriskLabel>
                  </MDTypography>
                </MDBox>
                <MDBox>
                  <MDButton
                    color="gray80"
                    size="small"
                    onClick={handleClose}
                    sx={{ mt: 2, mr: 1, position: "relative" }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    color="primaryRed"
                    size="small"
                    type="submit"
                    sx={{ mt: 2 }}
                    disabled={userToEdit ? isLoading || !dirty : isLoading}
                  >
                    {!isLoading
                      ? userToEdit
                        ? allowAccess
                          ? "Allow Access"
                          : "Update User"
                        : "Create new user"
                      : "Loading..."}
                  </MDButton>
                </MDBox>
              </MDBox>
            </Form>
          )}
        </Formik>
      </MDBox>
    </Modal>
  );
}

export default AddTeamMemberModal;
